
import { AssignMemberListModal, AudioIconPopupZoom, CommunicationNote } from 'components';
import { useAuthContext } from 'contexts';
import type { Communication, Patient } from 'fhir/r4';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Confirm, Icon, Item, Loader, Message, Modal, Popup, Table } from 'semantic-ui-react';
import { fhirPut } from 'services';
import type { ZoomCallRecord } from 'services/connect';
import type { CommunicationData, CommunicationEntry, ZoomEmailMessage } from 'types';
import { CommunicationHelper, ErrorResponse, handleOutboundCall, parseFHIRPatientName, sanitizeEmailHtml } from 'utils';

import * as Connect from '../../../../../services/connect';

type transferLineLogRowProps = {
  communicationResource: CommunicationEntry['resource'];
  communicationsMutate: () => Promise<CommunicationData | undefined>;
  handleErrorMessage: (errorMessage: string) => void;
  archived: boolean;
};

const TransferLineLogRow: React.FC<transferLineLogRowProps> = ({
  communicationsMutate,
  communicationResource,
  handleErrorMessage,
  archived,
}) => {
  const { practitionerId, practitionerName, token } = useAuthContext();

  const [_callbackClickError, setCallbackClickError] = useState<string>('');
  const [copyClickError, setCopyClickError] = useState<string>('');
  // Seeded `undefined` but reset with `null` by the delete handler and the confirm
  // dialog's cancel, so the state really holds both spellings of absent.
  const [deleteCall, setDeleteCall] = useState<Communication | null | undefined>(undefined);
  const [isAssignMemberModalOpen, setIsAssignMemberModalOpen] = useState<boolean>(false);
  const [_isCallbackClickSuccess, setIsCallbackClickSuccess] = useState(false);
  const [isCopyClickSuccess, setIsCopyClickSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Reset with `null` on close, so the state genuinely holds all three.
  const [selectedCallerCard, setSelectedCallerCard] = useState<Patient | null | undefined>(undefined);
  const [selectedCallTicket, setSelectedCallTicket] = useState<CommunicationHelper | null>(null);
  const [ticketPhoneNumber, setTicketPhoneNumber] = useState<string | undefined>('');
  const [data, setData] = useState<ZoomCallRecord>(null);
  const [open, setOpen] = useState(false);

  const handleAssignClick = (communicationData: CommunicationHelper) => {
    setSelectedCallTicket(communicationData);
    setIsAssignMemberModalOpen(true);
  };

  const handleAssignMember = async (communicationData: CommunicationHelper) => {
    setIsSubmitting(true);

    communicationData.setUknownCallerStatus(false);
    communicationData.setState('assigned');

    const fhirProxyClient = new FHIRProxyClient(token, `${import.meta.env.VITE_API_ENDPOINT}/fhir/`);

    const actorReference = { reference: `Practitioner/${practitionerId}`, display: practitionerName };
    // `managingOrganization` and its `reference` are both optional and read
    // unguarded here, as is `selectedCallerCard` itself.
    const orgId = selectedCallerCard!.managingOrganization!.reference!.split('/')[1];
    communicationData.setIdentifierOrgId(orgId!);
    const organizationReference = { code: orgId?.toUpperCase(), display: orgId?.toLowerCase() };
    const patientId = selectedCallerCard!.id;
    const inboundPayload = communicationData.getResource();
    const targetReference = `Communication/${inboundPayload.id}`;

    inboundPayload.subject = { reference: `Patient/${patientId}` };
    inboundPayload.sender = { display: parseFHIRPatientName(selectedCallerCard) };

    const provenance = ProvenanceBuilder.build(
      targetReference,
      actorReference,
      patientId,
      'UPDATE',
      'Transfer ticket has been updated to this member',
      organizationReference
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([inboundPayload], provenance);
    try {
      await fhirProxyClient.send('post', '', fhirBundle);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationsMutate();
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      handleErrorMessage(errorResponse);
    } finally {
      setIsSubmitting(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setIsAssignMemberModalOpen(false);
    setSelectedCallTicket(null);
    setSelectedCallerCard(null);
  };

  const handleDeleteCall = async (communicationResource: Communication) => {
    // Change identifier to deleted
    const helper = new CommunicationHelper(communicationResource);

    helper.setState('deleted');
    helper.setSubject(null);
    helper.setUknownCallerStatus(true);

    // Now post bundle request
    setIsSubmitting(true);

    try {
      await fhirPut('/Communication/' + helper.getId(), token, helper.getResource());

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationsMutate();
      setIsSubmitting(false);
      setDeleteCall(null);
    // eslint-disable-next-line no-empty -- swallows the awaited write; the resets above are skipped, wedging the dialog. Upstream, KNOWN_BUGS.md CM-16.
    } catch { }
  };

  const isOutbound = (communicationResource: Communication): boolean => {
    return !communicationResource?.received;
  };

  // eslint-disable-next-line @typescript-eslint/require-await -- upstream writes this callback async; removing it would change the emit
  const _onCallbackClick = async (phoneNumber: string, orgId: string) => {
    setIsCallbackClickSuccess(false);
    setCallbackClickError('');

    try {
      // Make the phone call here! Add connect api call
      setTicketPhoneNumber(phoneNumber);
      handleOutboundCall(phoneNumber, orgId);
      setIsCallbackClickSuccess(true);
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setCallbackClickError(`Failed to call: ${errorResponse}`);
    }
  };

  const handleCopyClick = async (textToCopy: string) => {
    setIsCopyClickSuccess(false);
    setCopyClickError('');

    try {
      setTicketPhoneNumber(textToCopy);

      await navigator.clipboard.writeText(textToCopy);

      setIsCopyClickSuccess(true);
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setCopyClickError(`Failed to call: ${errorResponse}`);
    }
  };

  const handleSelectCallerCard = (patient: Patient) => {
    setSelectedCallerCard(patient);
  };
  const handleRecordsClick = async () => {
    const response = await Connect.connectGet('/hooks/zoom-fetch-call-record/' + id, token);
    setData(response.data);
  };
  const communicationHelper = new CommunicationHelper(communicationResource);
  const _callState = communicationHelper.getState();
  const communicationId = communicationHelper.getId();
  const communicationHelperResource = communicationHelper.getResource();
  const _contactId = communicationHelper.getContactId();
  const orgId = communicationHelper.getOrgId();
  const _phoneNumber = communicationHelper.getMemberPhoneNumber();
  const _direction =
    communicationHelper
      .getResource()
      .identifier?.find((ext) => ext.system === 'https://projectwell.io/fhir/identifiers/communication/direction')
      ?.value || 'N/A';
  const memberCommSource =
    communicationHelper.getCommunicationMediumChannelText() === 'phone' ||
      communicationHelper.getCommunicationMediumChannelText() === 'sms'
      ? communicationHelper.getMemberPhoneNumber()
      : communicationHelper.getCommunicationMediumChannelText() === 'email'
        ? communicationHelper.getMemberEmail()
        : 'N/A';
  //console.log('memberCommSource', memberCommSource, communicationHelper.getCommunicationMediumChannelText());
  const subject = communicationResource?.topic?.text;
  const _type = isOutbound(communicationResource) ? 'outbound' : 'inbound';
  const hasDeleteCall = Boolean(deleteCall);
  const callDisposition: string = communicationHelper.getDispositionDataCodeableConceptDisplay() ?? '';
  //const callContent:Object|string|Array<string> = communicationHelper.get
  const _ticketId = communicationHelper.getTicketId();
  // Extract Engagement Id from identifier (engagement-id)
  const id = communicationHelper.getEngagementId();
  //sender
  const sender = communicationHelper.getSender() || 'N/A';
  //Zoom read-only notes
  const _noteExtension = communicationHelper
    .getExtensions()!
    .find((ext) => ext.url === 'https://projectwell.io/fhir/zoom-web-hook/extensions/zoom-engagement-note' && ext?.extension);
  const transferedNumber = communicationHelper.getTransferredLineNumber();
  // const noteData = noteExtension?.extension?.find(
  //   (ext) => ext.url === 'https://projectwell.io/fhir/zoom-web-hook/extensions/zoom-engagement-note-data'
  // );
  // const zoomNoteText = noteData?.valueCodeableConcept?.text?.split('|')[0] || 'N/A';

  return (
    <>
      <Table.Row key={communicationResource.id}>
        {/* EngagementId & Actions */}
        <Table.Cell style={{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '160px', textOverflow: 'ellipsis' }}>
          {id && (
            <Popup
              trigger={
                <Icon name="copy" onClick={async () => await handleCopyClick(id)} style={{ cursor: 'pointer' }} />
              }
              on="click"
            >
              {ticketPhoneNumber === id && (
                <>
                  {!isCopyClickSuccess ? (
                    <Loader active inline />
                  ) : copyClickError ? (
                    <Message content={copyClickError} error />
                  ) : (
                    <>
                      <Icon name="check" color="green" /> Copied to clipboard
                    </>
                  )}
                </>
              )}
            </Popup>
          )}
          {id || 'N/A'}
        </Table.Cell>

        {/* Phone 1 (liekly extension number)*/}
        <Table.Cell>{transferedNumber}</Table.Cell>
        {/* Phone 2 (liekly member phone number) */}
        <Table.Cell>{memberCommSource}</Table.Cell>
        {/* Username of agent */}
        <Table.Cell>{sender}</Table.Cell>
        {/* Channel & Audio Actions */}
        {/* <Table.Cell>
          {communicationHelper.getCommunicationMediumChannelText()}
        </Table.Cell> */}

        {/* Communication Subject */}
        <Table.Cell style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '900px' }}>
          <Item.Description>
            {callDisposition}{' '}
            {
              <CommunicationNote
                communicationPayload={communicationHelperResource}
                refresh={communicationsMutate}
                subject={subject}
              />
            }
          </Item.Description>
        </Table.Cell>
        {/*Trasncript/recoriding*/}
        <Table.Cell>
          <Modal
            open={open}
            centered
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            size={typeof data === 'string' ? 'mini' : 'large'}
            style={{ width: typeof data === 'string' ? '300px' : undefined }}
            trigger={
              <Icon
                onClick={() => {
                  setOpen(!open);
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget records load; a rejection leaves the expanded panel empty with no error shown
                  handleRecordsClick();
                }}
                name="play"
                style={{ cursor: 'pointer' }}
                color="blue"
              ></Icon>
            }
            on="click"
            content={
              <>
                {(!data || data === undefined || data === null) && <Loader content="Loading data..." active />}
                {data && <ContentRenderer data={data} />}
                {/* {(!data || data === undefined) && (
                  <Message error content="Oops! Could not find transcript/recording..." />
                )} */}
              </>
            }
          />
        </Table.Cell>
        {/* Timestamp */}
        <Table.Cell>
          {moment
            .utc(communicationResource?.sent || communicationResource.received)
            .local()
            .format('YYYY-MM-DD hh:mm:ss a')}
        </Table.Cell>

        {/* Actions */}
        <Table.Cell>
          <Button color="blue" onClick={() => handleAssignClick(communicationHelper)}>
            Assign
          </Button>
          {!archived && (
            <>
              <Icon
                name="archive"
                color="red"
                style={{ cursor: 'pointer' }}
                onClick={() => setDeleteCall(communicationResource)}
              />
              <Confirm
                open={hasDeleteCall}
                content="Are you sure you want to archive this communication?"
                cancelButton="Never mind"
                confirmButton={
                  <Button color="blue" loading={isSubmitting}>
                    Yes, archive it
                  </Button>
                }
                onCancel={() => setDeleteCall(null)}
                onConfirm={() => handleDeleteCall(deleteCall!)}
              />
            </>
          )}
        </Table.Cell>
      </Table.Row>
      {/* Assign Member Modal */}
      {selectedCallTicket?.getId() === communicationId && isAssignMemberModalOpen && (
        <AssignMemberListModal
          handleAssignMember={handleAssignMember}
          handleCloseModal={handleCloseModal}
          handleSelectCallerCard={handleSelectCallerCard}
          isAssignMemberModalOpen={isAssignMemberModalOpen}
          isSubmitting={isSubmitting}
          orgId={orgId!}
          selectedCallTicket={selectedCallTicket}
          selectedCallerCard={selectedCallerCard}
          searchQuery={memberCommSource}
        />
      )}
    </>
  );
};
function ContentRenderer({ data }: { data: ZoomCallRecord }) {
  return (
    <>
      {Array.isArray(data) && (
        <>
          <div style={{ maxHeight: 400, overflowY: 'auto', padding: '1rem' }}>
            {data.map((line, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: '1rem',
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '0.5rem',
                }}
              >
                {line ? <div>{line}</div> : <div style={{ fontStyle: 'italic', color: '#999' }}>[empty]</div>}
              </div>
            ))}
          </div>
        </>
      )}
      {typeof data === 'object' && !Array.isArray(data) && (
        <>
          {/*
            `typeof null === 'object'`, so this branch admits `null` and the assertion
            records that. Harmless only because the sole caller renders `ContentRenderer`
            under a `data &&` guard; the branch itself does not exclude it.
          */}
          <RenderEmail data={data!} />
        </>
      )}
      {typeof data === 'string' && (
        <>
          <AudioIconPopupZoom data={data} />
        </>
      )}
    </>
  );
}

function RenderEmail({ data }: { data: { messages?: ZoomEmailMessage[] } }) {
  return (
    <div style={{ maxHeight: 400, overflowY: 'auto', padding: '1rem' }}>
      {data.messages!.map((msg, idx) => (
        <div key={idx} style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>
          <div style={{ fontWeight: 'bold' }}>{msg.user_name}</div>
          <div style={{ fontSize: '0.8em', color: '#555' }}>{new Date(msg.sent_time!).toLocaleString()}</div>

          {msg.content ? (
            <div style={{ marginTop: '0.5rem' }} dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(msg.content) }} />
          ) : (
            <div style={{ marginTop: '0.5rem' }}>{msg.text}</div>
          )}

          {/*
            The `?.` here and the bare `.` on the `.map` below disagree about whether
            `inlineFiles` can be absent. Both assertions preserve what runs today:
            `undefined > 0` is `false`, so the absent case never reaches the `.map` and
            the missing guard there is latent rather than live. Left as written —
            reconciling the two spellings is a behaviour question, not an annotation.
          */}
          {(msg.inlineFiles?.length as number) > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8em', color: '#777' }}>
              <strong>Attachments:</strong>
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                {msg.inlineFiles!.map((f, i) => (
                  <li key={i}>
                    {f.file_name} ({f.file_type})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
export default TransferLineLogRow;
